import { DateFormats, formatDate } from '@strata/helper-functions';
import {
    type ColumnDef,
    type ColumnDefGeneric,
    type GridWithFilters,
    PrimaryData,
    SecondaryData,
} from '@strata/react-grid';
import { ServicingRate } from '@strata/react-servicing-rate';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';
import { type ComponentProps } from 'react';
import { type NavigateFunction } from 'react-router';

import { type ReferralSummary } from 'api/apertureAPI';
import { ReferralAndOnboardingStatusIndicator } from 'components/shared/ReferralAndOnboardingStatusIndicator';
import { ReferralRowMenu } from 'components/shared/ReferralRowMenu';
import { Permissions } from 'services/constants';
import { type BackToDestination } from 'services/navigationHelpers';
import { AppRoutes } from 'services/routes';

export const referralGridColumns = (
    permissions: string[],
    backToDestination: BackToDestination,
): ColumnDef<ReferralSummary>[] => [
    {
        accessorKey: 'member_id',
        header: 'Member ID',
        size: 150,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'product_service_description',
        header: 'Product',
        size: 120,
    },
    ...(permissions.includes(Permissions.READ_REFERRAL_RATES)
        ? [
              {
                  accessorKey: 'winning_bid_rate',
                  header: 'Referral Rate',
                  size: 100,
                  cell: ({ row, getValue }) => (
                      <SecondaryData
                          data={
                              <ServicingRate
                                  servicingRate={getValue()}
                                  referentPriceType={row.original.referent_price_type}
                              />
                          }
                      />
                  ),
              } as ColumnDefGeneric<ReferralSummary, 'winning_bid_rate'>,
          ]
        : []),
    {
        accessorKey: 'received_date',
        header: 'Date Received',
        size: 100,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'referral_status',
        header: 'Referral Status',
        enableSorting: false,
        size: 120,
        cell: ({ getValue, row }) => (
            <ReferralAndOnboardingStatusIndicator
                status={getValue()}
                enrollmentStatus={row.original.member_enrollment_status}
                referralId={row.original.referral_id}
            />
        ),
    },
    {
        accessorKey: 'treatment_id',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ getValue }) => (
            <ReferralRowMenu treatmentId={getValue()} backToDestination={backToDestination} />
        ),
    },
];

const isRowDisabled = (
    status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses],
) =>
    (
        [
            ReferralStatusIndicator.Statuses.BEN_DENIAL,
            ReferralStatusIndicator.Statuses.CANCELLED,
            ReferralStatusIndicator.Statuses.DENIED,
            ReferralStatusIndicator.Statuses.PENDAUTH,
        ] as (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses][]
    ).includes(status);

export const referralGridRowCssClass: ComponentProps<
    typeof GridWithFilters<ReferralSummary>
>['rowCssClass'] = (rowData) =>
    classNames({
        'griddle-row--disabled': isRowDisabled(rowData.referral_status),
    });

export const referralGridOnRowClick =
    (
        navigate: NavigateFunction,
        backToDestination: BackToDestination,
    ): ComponentProps<typeof GridWithFilters<ReferralSummary>>['onRowClick'] =>
    (rowData) => {
        if (!isRowDisabled(rowData.referral_status)) {
            navigate(AppRoutes.referralDetails(rowData.referral_id), {
                state: {
                    backToDestination,
                },
            });
        }
    };
